import React from 'react';
import { observer } from 'mobx-react';
import { CenteredLoadingSpinner, useFeature } from '@ole-ui/ole-ui-components';
import * as Sentry from '@sentry/react';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { handleError } from 'services/ErrorService';
import useMobxStores from 'hooks/useMobxStores';
import OfferingsListV2ErrorFallback from './V2/OfferingsListV2ErrorFallback';

const OfferingsListV2 = React.lazy(
  () => import(/* webpackChunkName: 'dashboard-offerings-v2' */ './V2'),
);

const LegacyOfferingsList = React.lazy(
  () => import(/* webpackChunkName: 'dashboard-offerings-legacy' */ './Legacy'),
);

const handleV2Error = (error: unknown) => {
  handleError(error as Error, { componentStack: 'OfferingsListV2' });
  Sentry.withScope((scope) => {
    scope.setTag('dashboard_offerings_version', 'v2');
    Sentry.captureException(error);
  });
};

const OfferingsList = () => {
  const { featuresStore } = useMobxStores();
  const isNewUserDashboardEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewUserDashboard,
    defaultFlagValue: false,
  });

  if (!featuresStore.allFeaturesFetched) {
    return (
      <div className="dashboard-offerings-loading">
        <CenteredLoadingSpinner />
      </div>
    );
  }

  return (
    <React.Suspense fallback={<CenteredLoadingSpinner />}>
      {isNewUserDashboardEnabled ? (
        <Sentry.ErrorBoundary
          fallback={<OfferingsListV2ErrorFallback />}
          onError={handleV2Error}
        >
          <OfferingsListV2 />
        </Sentry.ErrorBoundary>
      ) : (
        <LegacyOfferingsList />
      )}
    </React.Suspense>
  );
};

export default observer(OfferingsList);
